import type { MeasurementReviewField } from "./measurement-review";
import { consolidateOpeningReviewFields, withOpeningWidthFields } from "./opening-review-fields";
import { isWallHeightMeasurement } from "./wall-height-measurement";
import { shortMeasurementReviewLabel } from "./measurement-review-label";
import { checklistReviewFields } from "./checklist-review-fields";
import { keyMeasurementGroup } from "@workspace/api-zod/measurement-review";

export const MAX_SUGGESTED_MEASUREMENTS = 8;
const normal = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
const axis = (f: MeasurementReviewField) => {
  if (f.unit === "m²") return "area";
  return normal(f.label).match(/\b(length|width|height|depth|projection|area)\b(?!.*\b(?:length|width|height|depth|projection|area)\b)/)?.[1];
};
const supporting = /\b(?:demolish|demolition|existing|junction|foundations?|trenches?|footings?|drainage|parapets?|gutters?|padstones?|bearings?|plasterboard|skim|making good)\b/i;

/** Select UI suggestions only. Never filter original quote evidence or builder choices. */
export function keyMeasurementSuggestions(fields: MeasurementReviewField[], job: string) {
  fields = fields.map(f => f.original && /^(?:extension )?wall\s*\(?height\)?$/i.test(f.reviewLabel || "") &&
    !isWallHeightMeasurement(f) ? { ...f, label: f.original.label, reviewLabel: undefined, subjectLabel: undefined } : f);
  fields = consolidateOpeningReviewFields(fields);
  const keep = fields.filter(f => f.custom || f.edited || f.removed || f.original?.source === "entered");
  const extension = job === "Extension" || fields.some(f => f.subjectKind === "extension");
  const candidates = fields.filter(f => !keep.includes(f) &&
    // Subject assignment must not turn a window/elevation height into a key
    // extension measurement, including when restoring an older review.
    (!extension || axis(f) !== "height" || isWallHeightMeasurement(f) ||
      /\b(?:openings?|doorways?)\b/i.test(f.original?.label || f.label) &&
      !/\bwindows?\b/i.test(`${f.original?.label || f.label} ${f.original?.note || f.note}`)) &&
    (!extension || f.descriptionBasis || f.subjectKind === "opening" || /\bopening\b/i.test(f.label) ||
      !supporting.test(`${f.subjectLabel || ""} ${f.label}`)));
  const isFootprint = (f: MeasurementReviewField) =>
    (f.subjectKind === "extension" || /\bextension\b/i.test(f.label)) &&
    !/\b(?:roof|room|wall|opening|run|return|perimeter|footings?|foundations?|trench|excavation|windows?|doors?|beams?|steel)\b/i.test(f.label);
  const extensionOrder = (f: MeasurementReviewField) => {
    const a = axis(f);
    if (isFootprint(f) && a && /^(length|depth|projection|width|area)$/.test(a))
      return a === "area" ? 2 : a === "width" ? 1 : 0;
    if (/\brear wall\b/i.test(f.label)) return 3;
    if (/\bleft return\b/i.test(f.label)) return 4;
    if (/\bright return\b/i.test(f.label)) return 5;
    if (isWallHeightMeasurement(f) || f.wallHeightUnconfirmed) return 6;
    if (f.subjectKind === "opening" || /\bopening\b/i.test(f.label)) return 8;
    if (/\bwall\b.*\b(?:length|run|perimeter)\b/i.test(f.label)) return 7;
    return 9;
  };
  let selected: MeasurementReviewField[];
  if (extension) {
    const wallRuns = candidates.filter(f =>
      /\b(?:rear (?:wall|run)|(?:left|right)(?:[- ]hand)? return)\b/i.test(f.label) &&
      !/\b(?:opening|beam|foundations?|trench|footings?|nib)\b/i.test(f.label));
    const hasWallRuns = [...wallRuns, ...keep.filter(f =>
      /\b(?:rear wall|(?:left|right) return)\b/i.test(f.label))].some(f => f.value !== "");
    const footprints = candidates.filter(isFootprint);
    // Prefer the one proposed footprint with actual readings, not supporting blank pairs.
    const groups = new Map<string, MeasurementReviewField[]>();
    for (const f of footprints) {
      const key = f.subjectId || normal(f.label).replace(/\b(?:footprint|length|width|depth|projection|area)\b/g, "").trim();
      groups.set(key, [...(groups.get(key) || []), f]);
    }
    const primary = [...groups.values()].sort((a, b) =>
      b.filter(f => f.value !== "").length - a.filter(f => f.value !== "").length)[0] || [];
    const named = /\brear\b/i.test(primary.map(f => f.label).join(" ")) ? "Rear extension" : "Extension";
    selected = ["length", "width", "area"].flatMap(a => {
      if (keep.some(f => !f.custom && isFootprint(f) &&
        (axis(f) === a || a === "length" && /^(depth|projection)$/.test(axis(f) || "")))) return [];
      const matches = (f: MeasurementReviewField) => axis(f) === a ||
        a === "length" && /^(depth|projection)$/.test(axis(f) || "");
      const f = primary.find(matches) ||
        footprints.find(f => !f.subjectId && matches(f) && (a === "area" || !f.original)) ||
        (a !== "area" ? { id: `review-extension-${a}`, label: "", unit: "m" as const, value: "", note: "" } : undefined);
      const label = `${named} (${a === "area" ? "footprint area" : a})`;
      // Named external runs already describe the job. Do not put empty
      // rectangle questions ahead of those explicit sizes or infer a rectangle.
      if (hasWallRuns && !f?.value) return [];
      return f ? [{ ...f, ...(f.original ? { reviewLabel: label } : { label }), subjectLabel: undefined }] : [];
    });
    // Distinct stated wall runs/member lengths are primary job sizes, not a blank checklist.
    const stated = candidates.filter(f => wallRuns.includes(f) || f.descriptionBasis && f.openingEstimate)
      .sort((a, b) => Number(!!a.openingEstimate) - Number(!!b.openingEstimate));
    selected.push(...stated);
    const height = candidates.find(isWallHeightMeasurement);
    if (height) selected.push({ ...height, reviewLabel: "Extension Wall (height)", subjectLabel: undefined });
    const openings = candidates.filter(f =>
      (f.subjectKind === "opening" && /^(width|height)$/.test(axis(f) || "") || /\bopening\b/i.test(f.label)) &&
      !/\bwall\b.*\brun\b/i.test(f.original?.label || f.label));
    // Prefer known sizes from distinct openings over paired empty height questions.
    const known = openings.filter(f => f.value !== "" && !stated.includes(f));
    const main = known[0] || openings.find(f => !stated.includes(f));
    if (known.length > 1 || stated.some(f => f.openingEstimate)) selected.push(...known);
    else if (main) selected.push(...openings.filter(f => main.subjectId ? f.subjectId === main.subjectId : f === main).slice(0, 2));
  } else {
    // Filled readings take priority over supporting blank pairs; retain paired axes.
    const groups = new Map<string, MeasurementReviewField[]>();
    for (const f of candidates) {
      const key = f.subjectId || normal(f.label).replace(/\b(?:length|width|height|depth|projection|area)\b/g, "").trim();
      groups.set(key, [...(groups.get(key) || []), f]);
    }
    const primaryKind = ({ Openings: "opening", Loft: "loft", Roof: "roof", Walls: "wall",
      Groundworks: "groundworks", "External works": "external", Bathroom: "room", Kitchen: "room",
      "Room finishes": "room" } as Record<string, string>)[job] ||
      (candidates.some(f => f.subjectKind === "opening") ? "opening" : "");
    selected = [...groups.values()].sort((a, b) =>
      Number(b[0].subjectKind === primaryKind) - Number(a[0].subjectKind === primaryKind) ||
      b.filter(f => f.value !== "").length - a.filter(f => f.value !== "").length).flat().slice(0, 6);
  }
  // Order before capping as well as after adding preserved builder choices:
  // wall runs must not lose their suggestion slots to openings.
  if (extension) selected.sort((a, b) => extensionOrder(a) - extensionOrder(b));
  const seen = new Set(keep.map(f => f.id));
  const suggestions = selected.filter(f => {
    if (seen.has(f.id)) return false;
    seen.add(f.id);
    return true;
  }).slice(0, MAX_SUGGESTED_MEASUREMENTS);
  const suggestedIds = new Set(suggestions.map(f => f.id));
  const extras = checklistReviewFields(fields);
  const result = [...suggestions, ...extras.filter(f => !suggestedIds.has(f.id) && !keep.some(k => k.id === f.id)), ...keep]
    .filter((f, index, all) => all.findIndex(p => p.id === f.id) === index)
    .map(f => extension && /^wall\s*\(?height\)?$/i.test(f.reviewLabel || "") ?
    { ...f, reviewLabel: "Extension Wall (height)" } : f);
  // One card per heading: the same wall run or opening can arrive from the
  // description, drawing evidence and an older saved draft. A builder's own
  // change wins; otherwise the first (highest-priority) source is kept.
  const byHeading = new Map<string, MeasurementReviewField>();
  const ordered = extension ? result.sort((a, b) => extensionOrder(a) - extensionOrder(b)) : result;
  const rank = (f: MeasurementReviewField) => f.removed ? 4 : f.edited || f.original?.source === "entered" ? 3 : f.value !== "" ? 2 : 1;
  for (const f of ordered) {
    const group = keyMeasurementGroup(f.original?.label || f.label, f.unit, f.subjectKind);
    const scoped = ["doors", "windows", "rooms", "internal-walls", "steel", "roof", "floor"].includes(group || "");
    const key = f.custom ? `custom:${f.id}` : scoped ?
      `${f.subjectId || ""}|${normal(f.original?.label || f.label)}|${f.unit}` :
      `${shortMeasurementReviewLabel(f.reviewLabel || f.label).toLowerCase()}|${f.unit}`;
    const existing = byHeading.get(key);
    if (!existing || rank(f) > rank(existing)) byHeading.set(key, f);
  }
  const kept = new Set(byHeading.values());
  return withOpeningWidthFields(ordered.filter(f => kept.has(f)), fields);
}
